/**
 * The ONE way to turn an LOS `Address` object into display text.
 *
 * Resolution order:
 *   1. `fullAddress` — the geocoder's formatted string (only when it's a string;
 *      some LOS shapes carry a dict there).
 *   2. Assembled from the structured parts (`streetNumber streetName[, unit],
 *      city state zip`) when any of them is present.
 *   3. `displayAddress`, then `input_address` — what the user typed. The backend
 *      keeps these even when the geocode FAILS and every structured field comes
 *      back null (`geolocation == 'failed'`, or a swallowed geocoder error), so a
 *      saved-but-ungeocoded address still reads as an address, not as blank.
 *   4. `fallback` (default `null`, so callers can keep their own `||` chains).
 *
 * Use this instead of reading `address?.fullAddress` inline. The older
 * `formatAddressObjectFullAddress` / `formatAddressObject` delegate here.
 */
export const addressLabel = (address, fallback = null) => {
  if (!address || typeof address !== "object") return fallback;

  const full = address.fullAddress;
  if (typeof full === "string" && full.trim()) return full;

  const { streetNumber, streetName, city, state, zip } = address;
  const unit = address.unit || address.addressUnitNumber;
  const streetLine = [streetNumber, streetName].filter(Boolean).join(" ");
  const cityStateZip = [city, state, zip].filter(Boolean).join(" ");
  const assembled = [
    `${streetLine}${unit && streetLine ? `, ${unit}` : ""}`.trim(),
    cityStateZip,
  ]
    .filter(Boolean)
    .join(", ")
    .trim();
  if (assembled) return assembled;

  // Everything structured is empty — fall back to what the user typed.
  for (const key of ["displayAddress", "input_address"]) {
    const v = address[key];
    if (typeof v === "string" && v.trim()) return v.trim();
  }

  return fallback;
};

/** True when an address has nothing displayable at all. */
export const isAddressEmpty = (address) => addressLabel(address) === null;
